import {
  CheckboxItem,
  Content,
  Group,
  Item,
  ItemIndicator,
  Label,
  Portal,
  RadioGroup,
  RadioItem,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import { ComponentProps, createContext, useState } from 'react';

import { mergeClasses } from '@/utils';

import { CheckIcon, ChevronRightIcon, CircleIcon, DotsVerticalIcon } from '@/assets/icons';
import { Button } from '@/components/common';

interface IDropdownMenuContextValue {
  setOpen?: (open: boolean) => void;
}

const DropdownMenuContext = createContext<IDropdownMenuContextValue>({});

const DropdownMenu = ({
  modal = false,
  open = false,
  onOpenChange: onOpen,
  ...props
}: ComponentProps<typeof Root>) => {
  const [isOpen, setOpen] = useState(open);

  const onOpenChange = (value: boolean) => {
    setOpen(value);
    onOpen?.(value);
  };

  return (
    <DropdownMenuContext.Provider value={{ setOpen }}>
      <Root
        modal={modal}
        data-slot="dropdown-menu"
        open={isOpen}
        onOpenChange={onOpenChange}
        {...props}
      />
    </DropdownMenuContext.Provider>
  );
};

const DropdownMenuPortal = ({ ...props }: ComponentProps<typeof Portal>) => {
  return <Portal data-slot="dropdown-menu-portal" {...props} />;
};

const DropdownMenuTrigger = ({ children, ...props }: ComponentProps<typeof Trigger>) => {
  return (
    <Trigger data-slot="dropdown-menu-trigger" asChild {...props}>
      {children ?? (
        <Button
          buttonStyle="outline"
          buttonColor="neutral"
          borderRadius="soft"
          className="border-none !text-primary-darkest hover:bg-neutral-2 data-[state=open]:bg-neutral-2 transition-colors focus:shadow-none focus-visible:outline-none !ring-0"
          prefixIcon={<DotsVerticalIcon className="size-5" />}
        />
      )}
    </Trigger>
  );
};

const DropdownMenuContent = ({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof Content>) => {
  return (
    <Portal>
      <Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={mergeClasses(
          'bg-neutral-0 dark:bg-neutral-10 text-primary-darkest dark:text-neutral-0 font-medium text-body-sm [&_*:focus-visible]:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-base border dark:border-neutral-8 p-2 shadow-md',
          className,
        )}
        {...props}
      />
    </Portal>
  );
};

const DropdownMenuGroup = ({ ...props }: ComponentProps<typeof Group>) => {
  return <Group data-slot="dropdown-menu-group" {...props} />;
};

const DropdownMenuItem = ({
  className,
  inset,
  ...props
}: ComponentProps<typeof Item> & {
  inset?: boolean;
}) => {
  return (
    <Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      className={mergeClasses(
        "cursor-pointer focus:bg-primary-lightest dark:focus:!bg-neutral-8 p-3 [&_svg:not([class*='text-'])]:text-primary-base relative flex items-center gap-2 rounded-xs outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
};

const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ...props
}: ComponentProps<typeof CheckboxItem>) => {
  return (
    <CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={mergeClasses(
        "focus:bg-neutral-2 relative flex cursor-default items-center gap-2 rounded-small py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <ItemIndicator>
          <CheckIcon className="size-4" />
        </ItemIndicator>
      </span>
      {children}
    </CheckboxItem>
  );
};

const DropdownMenuRadioGroup = ({ ...props }: ComponentProps<typeof RadioGroup>) => {
  return <RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
};

const DropdownMenuRadioItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof RadioItem>) => {
  return (
    <RadioItem
      data-slot="dropdown-menu-radio-item"
      className={mergeClasses(
        "focus:bg-neutral-2 relative flex cursor-default items-center gap-2 rounded-small py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <ItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </ItemIndicator>
      </span>
      {children}
    </RadioItem>
  );
};

const DropdownMenuLabel = ({
  className,
  inset,
  ...props
}: ComponentProps<typeof Label> & {
  inset?: boolean;
}) => {
  return (
    <Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={mergeClasses('px-base py-s text-sm font-medium data-[inset]:pl-8', className)}
      {...props}
    />
  );
};

const DropdownMenuSeparator = ({ className, ...props }: ComponentProps<typeof Separator>) => {
  return (
    <Separator
      data-slot="dropdown-menu-separator"
      className={mergeClasses('bg-neutral-3 m-3 h-px', className)}
      {...props}
    />
  );
};

const DropdownMenuShortcut = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={mergeClasses('text-neutral-9 ml-auto text-xs tracking-widest', className)}
      {...props}
    />
  );
};

const DropdownMenuSub = ({ ...props }: ComponentProps<typeof Sub>) => {
  return <Sub data-slot="dropdown-menu-sub" {...props} />;
};

const DropdownMenuSubTrigger = ({
  className,
  inset,
  children,
  ...props
}: ComponentProps<typeof SubTrigger> & {
  inset?: boolean;
}) => {
  return (
    <SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={mergeClasses(
        "focus:bg-neutral-2 data-[state=open]:bg-neutral-2 data-[state=open]:text-neutral-9 [&_svg:not([class*='text-'])]:text-neutral-9 flex cursor-default items-center gap-2 rounded-small px-base py-s text-sm outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </SubTrigger>
  );
};

const DropdownMenuSubContent = ({ className, ...props }: ComponentProps<typeof SubContent>) => {
  return (
    <SubContent
      data-slot="dropdown-menu-sub-content"
      className={mergeClasses(
        'bg-neutral-0 text-primary-darkest font-medium text-body-sm [&_*:focus-visible]:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg',
        className,
      )}
      {...props}
    />
  );
};

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
};
